import * as Yup from "yup";
import { useFormik } from "formik";

import { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { Button, DatePicker, Label, MultiSelect } from "@/components/ui";
import { useFetchUsersQuery, useToast } from "@/hooks";

import { SheetFilterContainer } from "../../sheets";
import { DisplayError, formatDateToBackend } from "@/utils";

type FormFilterActivityLogsProps = PropsWithChildren

interface FilterValues {
  collaborator: string[];
  action: string;
  startDate: string;
  endDate: string;
}


export function FormFilterActivityLogs({ children }: FormFilterActivityLogsProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const { data: collaboratorsData, isLoading: collaboratorsLoading } =
    useFetchUsersQuery({});

  const actions = [
    {
      label: t('LABEL.all'),
      value: 'all',
    },
    {
      label: t('created'),
      value: 'created',
    },
    {
      label: t('updated'),
      value: 'updated',
    },
    {
      label: t('deleted'),
      value: 'deleted',
    },
  ]

  const initialValues: FilterValues = {
    collaborator: [],
    action: "all",
    startDate: "",
    endDate: "",
  };


  const {
    handleSubmit,
    getFieldProps,
    errors,
    touched,
    isSubmitting,
    resetForm,
    ...formik
  } = useFormik({
    initialValues,
    validationSchema: Yup.object().shape({
      collaborator: Yup.array().optional(),
      action: Yup.string().optional(),
      startDate: Yup.string().optional(),
      endDate: Yup.string().optional(),
    }),

    onSubmit: async (data) => {
      handleFilterActivityLogs(data)
    },
  });

  async function handleFilterActivityLogs(
    values: any
  ) {
    try {
      if (values.collaborator) {
        const ids = values.collaborator.join(",");
        queryParams.set("collaborator", ids)
      };
      if (values.action)
        queryParams.set("action", values.action);
      if (values.startDate)
        queryParams.set("startDate", formatDateToBackend(values.startDate));
      if (values.endDate)
        queryParams.set("endDate", formatDateToBackend(values.endDate));
      navigate({ search: queryParams.toString() });
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error),
        variant: "destructive",
      });
    }
  }

  function clearFilter() {
    queryParams.delete("collaborator");
    queryParams.delete("action");
    queryParams.delete("startDate");
    queryParams.delete("endDate");

    navigate({ search: queryParams.toString() });
    resetForm();
  }
  return (
    <SheetFilterContainer button={children}>
      <form className="space-y-10" onSubmit={handleSubmit}>
        <div className="space-y-3">

          <div className="space-y-2">
            <Label>Data inicial</Label>
            <DatePicker
              {...getFieldProps("startDate")}
              isInvalid={touched.startDate && !!errors.startDate}
              isValid={touched.startDate && !errors.startDate}
              messageError={errors.startDate}
            />
          </div>
          <div className="space-y-2">
            <Label>Data final</Label>
            <DatePicker
              {...getFieldProps("endDate")}
              isInvalid={touched.endDate && !!errors.endDate}
              isValid={touched.endDate && !errors.endDate}
              messageError={errors.endDate}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>Selecione o usuário</Label>
            <MultiSelect
              options={
                [
                  { value: "all", label: t("LABEL.all") },
                  ...(collaboratorsData?.items?.map((collaborator) => ({
                    value: collaborator.id,
                    label: collaborator.name,
                  })) || []
                  )]
              }
              loading={collaboratorsLoading}
              placeholder="Selecione um usuário..."
              isInvalid={
                !!touched.collaborator && !!errors.collaborator
              }
              isValid={
                touched.collaborator && !errors.collaborator
              }
              value={formik.values.collaborator}
              onChange={(newValue) => {
                const selected = newValue as string[];
                const wasAllSelected = formik.values.collaborator.includes("all");

                if (selected.includes("all") && !wasAllSelected) {
                  formik.setFieldValue("collaborator", ["all"]);
                  return;
                }

                if (wasAllSelected && selected.length > 1) {
                  const cleaned = selected.filter((v) => v !== "all");
                  formik.setFieldValue("collaborator", cleaned);
                  return;
                }

                formik.setFieldValue("collaborator", selected);
              }}


              onBlur={() => formik.handleBlur("collaborator")}
              messageError={errors.collaborator?.toString()}
              maxItems={3}
              multiple
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label>Selecione uma ação</Label>
            <MultiSelect
              options={actions}
              placeholder="Selecione uma ação..."
              emptyMessage="Nenhum ação encontrado"
              isInvalid={
                touched.action && errors.action
              }
              isValid={
                touched.action && !errors.action
              }
              value={formik.values.action}
              onChange={(value) =>
                formik.setFieldValue("action", value)
              }
              onBlur={() => formik.handleBlur("action")}
              messageError={errors.action}
            />
          </div>
        </div>

        <div className="flex justify-start gap-2">
          <Button type="submit" disabled={isSubmitting}>
            Filtrar
          </Button>
          <Button variant="secondary" type="button" onClick={clearFilter}>
            Limpar filtro
          </Button>
        </div>
      </form>
    </SheetFilterContainer>
  )
}